/* eslint-disable max-lines */
import { Button, Label } from '@hakimo-ui/shared/ui-base';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useState } from 'react';
import { useAlarmGroupTime } from '../../unified-alarm-group/alarm-provider/contexts';
import { GroupedFace, UnauthorizedFace } from '../util';
import { FaceImage, UNAUTHORIZED_FACE_GUIDELINE_URL } from '../utils';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface UnauthorizedFaceSeekDetection {
  timestamp: number;
  alarmId?: string;
}

interface Props {
  faces: UnauthorizedFace[];
  timeZone: string;
  onSeek?: (detection: UnauthorizedFaceSeekDetection) => void;
}

export function UnauthorizedFaceDetection({ faces, timeZone, onSeek }: Props) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const { seekTo, isSeekerPaused, toggleSeekerPaused } = useAlarmGroupTime();

  if (!faces || faces.length === 0) {
    return null;
  }

  // Group faces by profile_id
  const groupedFaces: GroupedFace[] = [];
  const faceMap = new Map<string, GroupedFace>();
  const latestTsMap = new Map<string, number>();

  faces.forEach((face) => {
    const key = face.profile_id;
    if (!faceMap.has(key)) {
      faceMap.set(key, {
        profile_id: face.profile_id,
        profile_name: face.profile_name,
        employee_id: face.employee_id,
        image_path: face.image_path,
        gender: face.gender,
        frame_url: face.frame_url,
        face_box: face.face_box,
        full_body_image_path: face.full_body_image_path,
        person_box: face.person_box,
        detections: [],
      });
      latestTsMap.set(key, face.timestamp);
    }
    const group = faceMap.get(key)!;
    group.detections.push({
      timestamp: face.timestamp,
      cameraName: face.cameraName,
      alarmId: face.alarmId,
    });
    if (face.timestamp >= (latestTsMap.get(key) ?? -Infinity)) {
      latestTsMap.set(key, face.timestamp);
      group.frame_url = face.frame_url;
      group.face_box = face.face_box;
      group.full_body_image_path = face.full_body_image_path;
      group.person_box = face.person_box;
    }
  });

  faceMap.forEach((group) => {
    group.detections.sort((a, b) => b.timestamp - a.timestamp);
  });

  groupedFaces.push(...Array.from(faceMap.values()));

  const toggleExpanded = (profileId: string) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(profileId)) {
      newExpanded.delete(profileId);
    } else {
      newExpanded.add(profileId);
    }
    setExpandedIds(newExpanded);
  };

  const handleTimestampClick = (detection: UnauthorizedFaceSeekDetection) => {
    if (onSeek) {
      onSeek(detection);
      return;
    }
    seekTo(detection.timestamp);
    if (!isSeekerPaused) {
      toggleSeekerPaused();
    }
  };

  const formatTimestamp = (timestamp: number) => {
    return dayjs.utc(timestamp).tz(timeZone).format('HH:mm:ss');
  };

  return (
    <div className="py-2">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-alert-600 font-sans text-base font-semibold">
          Unauthorized Person Detected
        </span>
        <a
          href={UNAUTHORIZED_FACE_GUIDELINE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 dark:text-primary-500 text-sm"
        >
          Guidelines
        </a>
      </div>
      <div className="scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 max-h-[140px] space-y-2 overflow-y-auto pr-2">
        {groupedFaces.map((group) => {
          const key = group.profile_id;
          const isExpanded = expandedIds.has(key);
          const additionalCount = group.detections.length - 1;
          const firstDetection = group.detections[0];

          return (
            <div key={key} className="space-y-1">
              <div className="flex items-center gap-2">
                <FaceImage
                  imagePath={group.image_path}
                  profileName={group.profile_name}
                  employeeId={group.employee_id}
                  gender={group.gender}
                  timestamp={firstDetection.timestamp}
                  cameraName={firstDetection.cameraName}
                  frameUrl={group.frame_url}
                  faceBox={group.face_box}
                  fullBodyImagePath={group.full_body_image_path}
                  personBox={group.person_box}
                />

                <div className="flex-1">
                  <div className="text-onlight-text-1 dark:text-ondark-text-1 flex flex-wrap items-center gap-2 text-sm font-medium">
                    <span>
                      {group.profile_name}
                      {group.employee_id && ` / EMP ID: ${group.employee_id}`}
                    </span>
                    {group.gender && (
                      <Label type="default" text={group.gender} small />
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Button
                      variant="link"
                      onClick={() => handleTimestampClick(firstDetection)}
                      className="!p-0 text-xs"
                    >
                      {formatTimestamp(firstDetection.timestamp)}
                    </Button>
                    {firstDetection.cameraName && (
                      <span className="text-onlight-text-2 dark:text-ondark-text-2">
                        {firstDetection.cameraName}
                      </span>
                    )}
                  </div>
                </div>

                {additionalCount > 0 && (
                  <Button
                    variant="icon"
                    onClick={() => toggleExpanded(key)}
                    className="!p-0 text-xs"
                  >
                    <span>+{additionalCount}</span>
                    {isExpanded ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>

              {isExpanded && additionalCount > 0 && (
                <div className="ml-12 space-y-1">
                  {group.detections.slice(1).map((detection, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs">
                      <Button
                        variant="link"
                        onClick={() => handleTimestampClick(detection)}
                        className="!p-0 text-xs"
                      >
                        {formatTimestamp(detection.timestamp)}
                      </Button>
                      {detection.cameraName && (
                        <span className="text-onlight-text-2 dark:text-ondark-text-2">
                          {detection.cameraName}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default UnauthorizedFaceDetection;
